import React, { useEffect, useState, FormEvent, ChangeEvent } from "react";

/**
 * Contact.tsx (AZMPN format)
 * ------------------------------------------------------------------
 * Converted from Azosis Technologies contact.tsx into the AZMPN
 * Tailwind-based layout/styling pattern.
 *
 * IMPORTANT: All original fields, validation, submit logic, office
 * data, and API call behavior from the Azosis version are preserved
 * 1:1 — only the presentation layer (markup + styling) has been
 * translated from the custom scoped <style> block into Tailwind
 * utility classes matching the AZMPN design language (bg-azmpn-dark,
 * #DAA464 accent, rounded-xl cards, etc).
 *
 * Notes for integration:
 * 1. Remix Icon (ri-*) is loaded dynamically via useEffect, same as
 *    the original. Remove this and add the <link> to your index.html
 *    <head> instead if you prefer.
 * 2. The form posts to the relative "/contact" endpoint (same pattern
 *    as the AZMPN template), so it works as-is behind whatever proxy/
 *    rewrite your app already has set up. Change the path below if
 *    your backend route differs.
 * 3. This component does NOT render its own header/footer (matching
 *    the AZMPN template you provided), since AZMPN pages already
 *    have shared layout components. If you need the Azosis-style
 *    in-page header/footer, let me know and I can add them back in.
 */

interface OfficeInfo {
  name: string;
  address: string;
}

const globalOffices: OfficeInfo[] = [
  {
    name: "Head Office",
    address:
      "c/o Wework Galazy 43, <br /> Residency Road, <br /> Bengaluru 560025",
  },
  {
    name: "Branch Office",
    address:
      "c/o Novel Office, Marathahalli, <br /> Old HAL Road, <br /> Bengaluru 560037",
  },
];
interface ContactFormState {
  fullName: string;
  company: string;
  email: string;
  phone: string;
  country: string;
  service: string;
  message: string;
}

const initialFormState: ContactFormState = {
  fullName: "",
  company: "",
  email: "",
  phone: "",
  country: "",
  service: "",
  message: "",
};

type SubmitStatus = "idle" | "submitting" | "success" | "error";

export default function Contact() {
  const [formData, setFormData] = useState<ContactFormState>(initialFormState);
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [errorText, setErrorText] = useState(
    "Something went wrong. Please try again or email us directly at letstalk@azosis.com.",
  );

  // Load Remix Icon font if it isn't already present on the page.
  useEffect(() => {
    const existing = document.querySelector('link[href*="remixicon"]');
    if (!existing) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href =
        "https://cdn.jsdelivr.net/npm/remixicon@4.2.0/fonts/remixicon.css";
      document.head.appendChild(link);
    }
  }, []);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("submitting");

    try {
      const res = await fetch("/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Submission failed");

      setStatus("success");
      setFormData(initialFormState);
    } catch (err) {
      setErrorText(
        err instanceof Error && err.message
          ? err.message
          : "Something went wrong. Please try again or email us directly at letstalk@azosis.com.",
      );
      setStatus("error");
    }
  };

  const isSubmitting = status === "submitting";

  return (
    <>
      <section className="azmpn-contact-section pt-28 pb-16 md:pt-36 md:pb-20 bg-azmpn-dark">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* HERO / HEADER */}
          <div className="text-center mb-10 max-w-3xl mx-auto">
            <span className="text-[#DAA464] text-xs font-bold tracking-widest uppercase mb-3 block">
              Get In Touch
            </span>

            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Let&apos;s talk about your{" "}
              <span className="text-[#DAA464]">business challenges.</span>
            </h2>

            <p className="text-white/60 max-w-3xl mx-auto mb-6">
              Whether you&apos;re planning a migration, modernising workspaces,
              or looking for managed technology services - our team is ready to
              help.
            </p>
          </div>

          {/* FORM + OFFICE GRID */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 max-w-6xl mx-auto">
            {/* FORM */}
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                Book a discovery workshop
              </h3>
              <p className="text-white/60 text-sm mb-6">
                Fill in the form and our team will get back to you within one
                business day.
              </p>

              <form
                onSubmit={handleSubmit}
                className="bg-white/5 p-6 rounded-xl space-y-4"
              >
                <div>
                  <label
                    htmlFor="fullName"
                    className="block text-white/60 text-sm mb-2"
                  >
                    Full Name <span className="text-[#DAA464]">*</span>
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    required
                    placeholder="Your full name"
                    value={formData.fullName}
                    onChange={handleChange}
                    className="w-full p-3 rounded-lg bg-white/10 text-white outline-none placeholder:text-white/30"
                  />
                </div>

                <div>
                  <label
                    htmlFor="company"
                    className="block text-white/60 text-sm mb-2"
                  >
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    placeholder="Your company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full p-3 rounded-lg bg-white/10 text-white outline-none placeholder:text-white/30"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-white/60 text-sm mb-2"
                  >
                    Business Email <span className="text-[#DAA464]">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full p-3 rounded-lg bg-white/10 text-white outline-none placeholder:text-white/30"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-white/60 text-sm mb-2"
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder="+1 (555) 000-0000"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full p-3 rounded-lg bg-white/10 text-white outline-none placeholder:text-white/30"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="country"
                      className="block text-white/60 text-sm mb-2"
                    >
                      Country / Region
                    </label>
                    <input
                      type="text"
                      id="country"
                      name="country"
                      placeholder="e.g. India, USA, UAE, Singapore"
                      value={formData.country}
                      onChange={handleChange}
                      className="w-full p-3 rounded-lg bg-white/10 text-white outline-none placeholder:text-white/30"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="service"
                    className="block text-white/60 text-sm mb-2"
                  >
                    Service Interested In{" "}
                    <span className="text-[#DAA464]">*</span>
                  </label>
                  <div className="relative">
                    <select
                      id="service"
                      name="service"
                      required
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full p-3 rounded-lg bg-white/10 text-white outline-none appearance-none cursor-pointer"
                    >
                      <option
                        value=""
                        disabled
                        className="text-gray-500 bg-white"
                      >
                        Select a service...
                      </option>

                      <option
                        value="Cloud Migration"
                        className="bg-white text-black"
                      >
                        Cloud Migration
                      </option>

                      <option
                        value="Digital Workspace"
                        className="bg-white text-black"
                      >
                        Digital Workspace
                      </option>

                      <option
                        value="Managed IT Services"
                        className="bg-white text-black"
                      >
                        Managed IT Services
                      </option>

                      <option
                        value="Technology Consulting"
                        className="bg-white text-black"
                      >
                        Technology Consulting
                      </option>

                      <option
                        value="Security & Compliance"
                        className="bg-white text-black"
                      >
                        Security & Compliance
                      </option>

                      <option value="Other" className="bg-white text-black">
                        Other
                      </option>
                    </select>
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#DAA464]">
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-white/60 text-sm mb-2"
                  >
                    How can we help?
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="Tell us about your project, goals, or challenges..."
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full p-3 rounded-lg bg-white/10 text-white outline-none resize-none placeholder:text-white/30"
                  ></textarea>
                  <p className="text-white/30 text-xs mt-1">
                    Maximum 500 characters
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#DAA464] py-3 rounded-lg font-semibold text-black hover:bg-[#c4913a] transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>
                    {isSubmitting ? "Submitting..." : "Submit Inquiry"}
                  </span>
                  <i
                    className={
                      isSubmitting
                        ? "ri-loader-4-line animate-spin"
                        : "ri-arrow-right-line"
                    }
                  ></i>
                </button>

                {status === "success" && (
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-green-900/20 border border-green-700/40 text-green-300 text-sm">
                    <i className="ri-checkbox-circle-line mt-0.5"></i>
                    <p className="m-0">
                      Thank you. Our team will contact you shortly.
                    </p>
                  </div>
                )}
                {status === "error" && (
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-red-900/20 border border-red-700/40 text-red-300 text-sm">
                    <i className="ri-error-warning-line mt-0.5"></i>
                    <p className="m-0">{errorText}</p>
                  </div>
                )}
              </form>
            </div>

            {/* OFFICE CARDS */}
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-white mb-8">
                Contact Azosis
              </h3>

              <div className="space-y-4">
                

                {/* OFFICE ADDRESS CARD */}
                {globalOffices.map((office) => (
                  <div
                    key={office.name}
                    className="bg-white/5 border border-white/10 hover:border-[#DAA464]/40 transition-colors p-6 rounded-xl"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-[#DAA464]/10 border border-[#DAA464]/30 text-[#DAA464] text-lg rounded-lg">
                        <i className="ri-building-2-line"></i>
                      </div>

                      <div>
                        <h4 className="font-semibold text-[#DAA464] mb-1">
                          {office.name}
                        </h4>

                        <p
                          className="text-white/60 text-sm leading-relaxed mb-3"
                          dangerouslySetInnerHTML={{ __html: office.address }}
                        ></p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fallback scoped styles — only kick in for tokens/utilities that may
        not exist in this project's Tailwind config yet (e.g. bg-azmpn-dark).
        These are additive and scoped under .azmpn-contact-section, so they
        won't leak out or fight with Tailwind classes that ARE configured. */}
      <style>{`
      .azmpn-contact-section {
        --azmpn-dark: #0a0a0a;
        --azmpn-gold: #DAA464;
        --azmpn-goldDark: #c4913a;
      }
      .azmpn-contact-section.bg-azmpn-dark {
        background-color: var(--azmpn-dark);
      }
      .azmpn-contact-section input,
      .azmpn-contact-section select,
      .azmpn-contact-section textarea {
        font-family: inherit;
      }
      .azmpn-contact-section input:focus,
      .azmpn-contact-section select:focus,
      .azmpn-contact-section textarea:focus {
        outline: none;
        box-shadow: 0 0 0 1px rgba(218, 164, 100, 0.5);
      }
      .azmpn-contact-section select {
        color-scheme: dark;
      }
      .azmpn-contact-section .animate-spin {
        animation: azmpn-spin 1s linear infinite;
      }
      @keyframes azmpn-spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `}</style>
    </>
  );
}
